import { OutlinedInput, InputAdornment, Stack, Chip, Button } from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import { workflowService } from "../../../../Core/Api/services";
import { ModalContext } from "../../../../Components/Modals";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DbWorkflowDefinitionInfos } from "../../../../Core/Api/Types/db-workflow-definition-infos";
import ModalWorkflowModelEdit from "../../../../Components/Modals/ModalWorkflowModelEdit/modal-workflow-model-edit";
import { useTranslation } from "react-i18next";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsWorkflows() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const modalContext = useContext(ModalContext);

    const { openModal } = { ...modalContext };
    const enqueueCustomSnackbar = useCustomSnackbar();

    const [filterText, setFilterText] = useState("");
    const [rows, setRows] = useState<DbWorkflowDefinitionInfos[]>([]);

    useEffect(() => {
        workflowService.GetDefinitions().then((data) => {
            setRows(data.definitions ?? []);
            setIsLoading(false);
        });
    }, []);

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "Id",
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
            },
            {
                field: "name",
                headerName: t("common:name"),
                flex: CellSize.small,
                minWidth: CellSize.small,
            },
            {
                field: "family",
                headerName: t("common:family"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => <Chip label={data.value ?? "-"} sx={{ mt: 0.5, mb: 0.5 }} />,
            },
            {
                field: "type",
                headerName: t("common:type"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => <Chip label={data.value ?? "-"} />,
            },
            {
                field: "engine",
                headerName: t("common:engine"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => <Chip label={data.value ?? t("chip:label.defaultDpm")} />,
            },
            {
                field: "licenseSlots",
                headerName: t("common:licences"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => <Chip label={data.value ?? t("common:unlimited")} />,
            },
            {
                field: "filter",
                headerName: t("common:filtering"),
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                valueGetter: (params: any, row: any) => row?.filter !== null,
                renderCell: (data: Row) => <TableCellBool value={data.value} />,
            },
            {
                field: "userIdReferenceApi",
                headerName: t("table:headerName.refUser"),
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                valueGetter: (params: any, row: any) => row?.userIdReferenceApi !== null,
                renderCell: (data: Row) => <TableCellBool value={data.value} />,
            },
            {
                headerName: "",
                field: "actions",
                type: "actions",
                align: "right",
                getActions: (params: GridRowParams) => {
                    if (!params || !params.row) return;
                    return [
                        <GridActionsCellItem
                            icon={<ModeEditOutlineOutlinedIcon color="primary" />}
                            label={t("common:editUpperCase")}
                            onClick={() => openEditModal(false, params.row)}
                        />,
                        <GridActionsCellItem
                            icon={<DeleteOutlineOutlined color="error" />}
                            label={t("common:delete")}
                            onClick={() => handleDelete(params?.row?.id)}
                        />,
                    ];
                },
            },
        ],
        [rows]
    );

    const openEditModal = useCallback(
        (isCreation: boolean, wf: DbWorkflowDefinitionInfos) => {
            let feedbackData = isCreation
                ? (newWF) => setRows([...rows, newWF])
                : (updatedWF) => setRows([...rows.filter((x) => x.id !== wf.id), updatedWF]);

            if (!openModal) return;
            openModal(ModalWorkflowModelEdit, { isCreation, wf: wf, feedbackData });
        },
        [openModal, rows]
    );

    const handleDelete = useCallback(
        (id: number) => {
            workflowService.DeleteDefinition(id).then((response) => {
                if (response.success) {
                    setRows(rows.filter((x) => x.id != id));
                    enqueueCustomSnackbar(t("alert:success.workflowModelDeleted"), "success");
                } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
            });
        },
        [rows]
    );

    const Table = useMemo(() => {
        return;
    }, [rows, isLoading, filterText]);

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
                openEditModal(true, new DbWorkflowDefinitionInfos());
            }}
        >
            {t("button:newWorkflowModel")}
        </Button>
    );

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_WORFKLOWS}
            columns={columns}
            rows={rows.filter(
                (x) =>
                    filterText === "" ||
                    x.name?.includesAllWords(filterText) ||
                    x.id.toString()?.includesAllWords(filterText) ||
                    x.type?.toString()?.includesAllWords(filterText)
            )}
            additionalActions={additionalActions}
            rowHeight={64}
            pagination={true}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
        />
    );
}

export default AdminSettingsWorkflows;
